import React, { useState } from 'react';
import { app } from './firebase';
import { getFirestore, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, getStorage } from 'firebase/storage';

const db = getFirestore(app);
const storage = getStorage(app);

export default function ConsultantsView({ consultantDb = [], activeContracts = [], customers = [], loading, isReadOnly }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Modal Tab
  const [activeTab, setActiveTab] = useState('profile'); // 'profile', 'assignment'

  // Form State
  const initialFormState = { 
    firstName: '', lastName: '', city: '', country: 'Belgium',
    dateOfBirth: '', careerStartDate: '',
    softSkills: '', hardSkills: '',
    status: 'Available', 
    email: '', phone: '',
    imageFile: '',
    notes: ''
  };
  const [formData, setFormData] = useState(initialFormState);

  // --- 1. DERIVE ACTIVE LIST ---
  const derivedConsultants = consultantDb.map(dbRecord => {
      const activeContract = activeContracts.find(c => c.consultantId === dbRecord.id && c.status === 'Active');
      if (!activeContract) return null;

      const customer = customers.find(cust => cust.companyName === activeContract.client);

      let experienceYears = 'N/A';
      if (dbRecord.careerStartDate) {
          const date = new Date(dbRecord.careerStartDate);
          const diff = Date.now() - date.getTime();
          const ageDate = new Date(diff); 
          experienceYears = Math.abs(ageDate.getUTCFullYear() - 1970) + " Years";
      }

      return {
          id: dbRecord.id,
          ...dbRecord,
          fullName: `${dbRecord.firstName || ''} ${dbRecord.lastName || ''}`.trim(),
          role: activeContract.jobTitle, 
          location: `${dbRecord.city || ''}, ${dbRecord.country || ''}`,
          experience: experienceYears,
          status: 'Busy', 
          client: activeContract.client,
          customerInfo: customer || null,
          contractStart: activeContract.startDate,
          contractEnd: activeContract.endDate,
          techStack: dbRecord.hardSkills || '',
          imageFile: dbRecord.imageFile || ''
      };
  }).filter(item => item !== null);

  // --- 2. FILTERING ---
  const filtered = derivedConsultants.filter(c => {
    const text = (c.fullName + c.role + c.techStack + (c.client || '')).toLowerCase();
    return text.includes(searchTerm.toLowerCase());
  });

  // --- HANDLERS ---

  const openDetailsModal = (c) => {
    setFormData({
        firstName: c.firstName || '', lastName: c.lastName || '',
        city: c.city || '', country: c.country || 'Belgium',
        dateOfBirth: c.dateOfBirth || '', careerStartDate: c.careerStartDate || '',
        softSkills: c.softSkills || '', hardSkills: c.hardSkills || '',
        status: c.status, email: c.email || '', phone: c.phone || '',
        imageFile: c.imageFile || '',
        notes: c.notes || '',
        // Include assignment info for display
        client: c.client,
        customerInfo: c.customerInfo,
        contractStart: c.contractStart,
        contractEnd: c.contractEnd,
        role: c.role
    });
    setSelectedFile(null);
    setIsEditing(false);
    setEditingId(c.id);
    setActiveTab('profile');
    setShowModal(true);
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) setSelectedFile(e.target.files[0]);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if(isReadOnly) return;
    setIsUploading(true);
    try {
      let imageUrl = formData.imageFile;

      if (selectedFile) {
        const storageRef = ref(storage, `consultant-profiles/${Date.now()}_${selectedFile.name}`);
        await uploadBytes(storageRef, selectedFile);
        imageUrl = await getDownloadURL(storageRef);
      }

      // Exclude assignment-only fields from saving back to DB
      const { client, customerInfo, contractStart, contractEnd, role, ...dbData } = formData;
      const finalData = { ...dbData, imageFile: imageUrl };

      Object.keys(finalData).forEach(key => finalData[key] === undefined && delete finalData[key]);

      if (editingId) {
        await updateDoc(doc(db, "consultant_db", editingId), finalData);
      }
      setIsEditing(false);
      alert("Profile updated successfully.");
    } catch (err) {
      console.error(err);
      alert("Error saving profile.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="container-fluid p-0">
      
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
            <h2 className="fw-bold text-main">Active Consultants</h2>
            <p className="text-muted-theme">Consultants currently placed on active contracts.</p>
        </div>
        <div className="d-flex gap-2">
          <input 
            type="text" 
            className="form-control" 
            placeholder="Search active team..." 
            onChange={(e) => setSearchTerm(e.target.value)} 
          />
        </div>
      </div>

      {/* GRID */}
      {loading ? <div className="text-main">Loading...</div> : (
        <div className="row g-4">
          {filtered.map((c) => (
            <div key={c.id} className="col-md-6 col-xl-4">
              <div 
                className="card h-100 bg-card border-theme shadow-sm"
                style={{ cursor: 'pointer' }}
                onClick={() => openDetailsModal(c)}
              >
                <div className="card-body d-flex flex-column">
                  
                  {/* HEADER */}
                  <div className="d-flex justify-content-between mb-3">
                    <div className="d-flex align-items-center">
                      {c.imageFile ? (
                        <img 
                          src={c.imageFile} 
                          alt={c.fullName} 
                          className="rounded-circle object-fit-cover border border-theme" 
                          style={{width: 50, height: 50}} 
                        />
                      ) : (
                        <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white fw-bold" style={{width: 50, height: 50, fontSize: 20}}>
                            {c.firstName ? c.firstName.charAt(0) : '?'}
                        </div>
                      )}
                      <div className="ms-3">
                        <h5 className="card-title mb-0 text-main">{c.fullName}</h5>
                        <small className="text-accent">{c.role}</small>
                      </div>
                    </div>
                    <span className="badge align-self-start bg-success">Active</span>
                  </div>

                  {/* DETAILS */}
                  <div className="mb-3 small text-muted-theme">
                    <div className="d-flex align-items-center mb-1"><i className="bi bi-geo-alt me-2"></i> {c.location}</div>
                    <div className="d-flex align-items-center mb-1"><i className="bi bi-briefcase me-2"></i> {c.experience} Exp</div>
                    <div className="d-flex align-items-center fw-bold text-main"><i className="bi bi-building me-2"></i> {c.client}</div>
                    {c.contractEnd && (
                        <div className="d-flex align-items-center mt-1 text-warning">
                            <i className="bi bi-hourglass-split me-2"></i> Ends: {c.contractEnd}
                        </div>
                    )}
                  </div>
                  
                  {/* TAGS */}
                  <div className="mb-0">
                    {c.techStack && c.techStack.split(',').slice(0, 4).map((t, idx) => (
                        <span key={idx} className="badge bg-input border border-theme me-1 text-muted-theme fw-normal">{t.trim()}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
              <div className="col-12 text-center text-muted-theme py-5">
                  <i className="bi bi-people fs-1 d-block mb-3"></i>
                  No active consultants found.<br/>
                  <small>Consultants will appear here once they are placed on an Active Contract.</small>
              </div>
          )}
        </div>
      )}

      {/* MODAL */}
      {showModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.8)'}}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content border-theme shadow-lg">
              <div className="modal-header border-theme">
                <div className="d-flex align-items-center gap-3">
                    {formData.imageFile ? (
                        <img src={formData.imageFile} className="rounded-circle object-fit-cover border border-theme" style={{width: 40, height: 40}} alt="Profile" />
                    ) : (
                        <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white fw-bold" style={{width: 40, height: 40}}>{formData.firstName?.charAt(0)}</div>
                    )}
                    <h5 className="modal-title text-main">{formData.firstName} {formData.lastName}</h5>
                </div>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>

              {/* TABS */}
              <ul className="nav nav-tabs border-theme px-3 pt-2 bg-input">
                  <li className="nav-item">
                      <button className={`nav-link ${activeTab === 'profile' ? 'active bg-card text-primary border-theme border-bottom-0' : 'text-muted-theme border-transparent'}`} onClick={() => setActiveTab('profile')}>Consultant Profile</button>
                  </li>
                  <li className="nav-item">
                      <button className={`nav-link ${activeTab === 'assignment' ? 'active bg-card text-primary border-theme border-bottom-0' : 'text-muted-theme border-transparent'}`} onClick={() => setActiveTab('assignment')}>Current Assignment</button>
                  </li>
              </ul>

              <div className="modal-body p-4">
                {activeTab === 'profile' && (
                    <form onSubmit={handleSave}>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="small text-muted-theme">First Name</label>
                                <input className="form-control" value={formData.firstName} onChange={e=>setFormData({...formData, firstName: e.target.value})} disabled={!isEditing} required/>
                            </div>
                            <div className="col-md-6">
                                <label className="small text-muted-theme">Last Name</label>
                                <input className="form-control" value={formData.lastName} onChange={e=>setFormData({...formData, lastName: e.target.value})} disabled={!isEditing} required/>
                            </div>
                            
                            <div className="col-md-6">
                                <label className="small text-muted-theme">Email</label>
                                <input className="form-control" value={formData.email} onChange={e=>setFormData({...formData, email: e.target.value})} disabled={!isEditing}/>
                            </div>
                            <div className="col-md-6">
                                <label className="small text-muted-theme">Phone</label>
                                <input className="form-control" value={formData.phone} onChange={e=>setFormData({...formData, phone: e.target.value})} disabled={!isEditing}/>
                            </div>

                            <div className="col-md-6">
                                <label className="small text-muted-theme">Location (City)</label>
                                <input className="form-control" value={formData.city} onChange={e=>setFormData({...formData, city: e.target.value})} disabled={!isEditing}/>
                            </div>
                            <div className="col-md-6">
                                <label className="small text-muted-theme">Date of Birth</label>
                                <input type="date" className="form-control" value={formData.dateOfBirth} onChange={e=>setFormData({...formData, dateOfBirth: e.target.value})} disabled={!isEditing}/>
                            </div>

                            <div className="col-12">
                                <label className="small text-muted-theme">Technical Stack (Keywords)</label>
                                <input className="form-control" value={formData.hardSkills} onChange={e=>setFormData({...formData, hardSkills: e.target.value})} disabled={!isEditing} placeholder="Java, AWS, React..."/>
                            </div>

                            <div className="col-12">
                                <label className="small text-muted-theme">Internal Notes</label>
                                <textarea className="form-control" rows="4" value={formData.notes} onChange={e=>setFormData({...formData, notes: e.target.value})} disabled={!isEditing} placeholder="Performance notes, career goals, etc..."></textarea>
                            </div>
                            
                            {isEditing && (
                                <div className="col-12 mt-3">
                                    <label className="small text-muted-theme">Update Profile Image</label>
                                    <input type="file" className="form-control" onChange={handleFileChange}/>
                                </div>
                            )}
                        </div>
                    </form>
                )}

                {activeTab === 'assignment' && (
                    <div className="row g-4">
                        {/* ASSIGNMENT HEADER */}
                        <div className="col-12">
                            <div className="bg-primary bg-opacity-10 p-3 rounded border border-primary border-opacity-25">
                                <div className="row align-items-center">
                                    <div className="col">
                                        <h6 className="text-primary fw-bold mb-1">Active Assignment</h6>
                                        <h4 className="text-main mb-0">{formData.role}</h4>
                                    </div>
                                    <div className="col-auto text-end">
                                        <span className="badge bg-success">Onsite / Hybrid</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* CUSTOMER INFO */}
                        <div className="col-md-6">
                            <h6 className="text-main fw-bold mb-3"><i className="bi bi-building me-2 text-accent"></i> Customer Details</h6>
                            {formData.customerInfo ? (
                                <div className="p-3 border border-theme rounded bg-input">
                                    <div className="fw-bold text-main fs-5 mb-1">{formData.customerInfo.companyName}</div>
                                    <div className="text-muted-theme small mb-3">{formData.customerInfo.industry} | {formData.customerInfo.type}</div>
                                    
                                    <div className="small mb-2">
                                        <div className="text-muted-theme">Contact Person:</div>
                                        <div className="text-main">{formData.customerInfo.contactName || 'N/A'}</div>
                                    </div>
                                    <div className="small mb-2">
                                        <div className="text-muted-theme">Address:</div>
                                        <div className="text-main">{formData.customerInfo.address || 'N/A'}</div>
                                    </div>
                                    {formData.customerInfo.environmentTags && (
                                        <div className="mt-2">
                                            {formData.customerInfo.environmentTags.split(',').map(tag => (
                                                <span key={tag} className="badge bg-secondary bg-opacity-25 text-muted-theme me-1 border border-theme" style={{fontSize: '0.7rem'}}>{tag.trim()}</span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="p-4 text-center border border-theme border-dashed rounded text-muted-theme">
                                    <i className="bi bi-exclamation-triangle d-block mb-2"></i>
                                    Customer record for "{formData.client}" not found.
                                </div>
                            )}
                        </div>

                        {/* CONTRACT DATES */}
                        <div className="col-md-6">
                            <h6 className="text-main fw-bold mb-3"><i className="bi bi-calendar-check me-2 text-accent"></i> Contract Timeline</h6>
                            <div className="p-3 border border-theme rounded bg-input h-100 d-flex flex-column justify-content-center">
                                <div className="text-center mb-3">
                                    <div className="small text-muted-theme">Start Date</div>
                                    <div className="fw-bold text-main fs-5">{formData.contractStart}</div>
                                </div>
                                <div className="text-center position-relative my-2">
                                    <div className="border-top border-theme w-100"></div>
                                    <i className="bi bi-arrow-down-circle position-absolute top-50 start-50 translate-middle bg-input px-2 text-muted-theme"></i>
                                </div>
                                <div className="text-center mt-3">
                                    <div className="small text-muted-theme">End Date</div>
                                    <div className="fw-bold text-warning fs-5">{formData.contractEnd}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
              </div>

              <div className="modal-footer border-theme">
                {activeTab === 'profile' && !isReadOnly && (
                    <div className="me-auto">
                        {!isEditing ? (
                            <button className="btn btn-outline-primary" onClick={() => setIsEditing(true)}><i className="bi bi-pencil me-2"></i> Edit Profile</button>
                        ) : (
                            <button className="btn btn-outline-secondary" onClick={() => setIsEditing(false)}>Cancel Edit</button>
                        )}
                    </div>
                )}
                
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                {isEditing && (
                    <button type="button" className="btn btn-success" onClick={handleSave} disabled={isUploading}>
                        {isUploading ? "..." : "Save Changes"}
                    </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
